import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { generateRandomString, getField } from 'src/common/helper';
import DataCalendarTimes from 'src/components/calendar/schedule/calendarPartials/DataCalendarTimes';
import DaysCalendarHeaderFirst from 'src/components/calendar/schedule/calendarPartials/DaysCalendarHeaderFirst';
import DaysCalendarHeaderFour from 'src/components/calendar/schedule/calendarPartials/DaysCalendarHeaderFour';
import DaysCalendarHeaderSecond from 'src/components/calendar/schedule/calendarPartials/DaysCalendarHeaderSecond';
import DaysCalendarHeaderThird from 'src/components/calendar/schedule/calendarPartials/DaysCalendarHeaderThird';
import DialogAddReserve, { dataType } from 'src/components/calendar/schedule/DialogAddReserve';
import DialogEditNote from 'src/components/calendar/schedule/DialogEditNote';
import PrintDateCalendar from 'src/components/calendar/schedule/PrintDateCalendar';
import { stickyCol, stickyHeader } from 'src/components/calendar/styles';
import {
  DataCalendarType,
  DataNoteType,
  DatePrintType,
} from 'src/components/calendar/TypeCustom';
import DialogDetail from 'src/components/reservationList/DialogDetail';
import { useStore } from 'src/context/StoreContext';
import './styles/calendar.css';

const colorBorderTable = '#DDDDDD';

const dataNoteDefine = {
  open: false,
  doctorid: 0,
  note: '',
  reservetype: 0,
  thedate: '',
  ts: '',
  typeid: 0,
};

const dataAddReservationDefine = {
  typeAdd: true,
  positionData: null,
  reserveEdit: null,
  patientEdit: null,
  indexColumn: -1
};

type CalendarProps = {
  dateSelected: string;
  dataCalendar: DataCalendarType;
  onChangeNote: (dataNote: DataNoteType, value: string) => void;
  openFilter: boolean;
  reloadCalendar?: () => void;
};

const Calendar: React.FC<CalendarProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;

  const [doctorsData, setDoctorsData] = useState<any[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataNote, setDataNote] = useState<DataNoteType>(dataNoteDefine);
  const [dataPrint, setDataPrint] = useState<DatePrintType>({
    datePrint: '',
    code: '',
  });
  const [waiting, setWaiting] = useState<boolean>(false);
  const [dataDialogDetail, setDataDialogDetail] = useState<boolean>(false);
  const [openDialogAddReserve, setOpenDialogAddReserve] =
    useState<boolean>(false);
  const [detailId, setDetailId] = useState(0);
  const [detailPatientid, setDetailPatientid] = useState(0);
  // const [timeAdd, setTimeAdd] = useState('');
  // const [typeAdd, setTypeAdd] = useState(true);
  // const [reserveEdit, setReserveEdit] = useState<any>({});
  // const [patientEdit, setPatientEdit] = useState<any>({});
  const [dataAddReservation, setDataAddReservation] = useState<dataType>(dataAddReservationDefine);

  const [dataNotes, setDataNotes] = useState<any>({});
  const [daysFormatIndex, setDaysFormatIndex] = useState<any[]>([]);

  const dataTimes = useMemo(() => props.dataCalendar.times, [props.dataCalendar.times]);
  const dataDays = useMemo(() => props.dataCalendar.days, [props.dataCalendar.days]);
  // const dataNotes = useMemo(() => props.dataCalendar.notes, [props.dataCalendar.notes]);

  const handleOpenDialogDetail = useCallback(
    (id: number, patientid: number, indexColumn: number) => {
      setDataAddReservation({
        ...dataAddReservation,
        indexColumn: indexColumn
      });

      setDetailId(id);
      setDetailPatientid(patientid);
      setDataDialogDetail(true);
    },
    // eslint-disable-next-line
    []
  );

  const handleCloseDialogDetail = useCallback(() => {
    setDetailId(0);
    setDataDialogDetail(false);
  }, []);

  const handleOpenDialogAddReserve = useCallback((indexColumn: number, time: string) => {
    setDataAddReservation({
      ...dataAddReservationDefine,
      indexColumn,
      positionData: {
        starttime: time,
        ...daysFormatIndex[indexColumn]
      }
    });

    setOpenDialogAddReserve(true);
  }, [daysFormatIndex]);

  const handleCloseDialogAddReserve = useCallback((isRefresh: boolean = false) => {
    if (isRefresh) {
      props.reloadCalendar?.();
    }

    setOpenDialogAddReserve(false);
    // eslint-disable-next-line
  }, []);

  const handleOpenDialogEditNote = useCallback(
    (data: any) => () => {
      setDataNote({
        ...data,
        open: true,
      });
    },
    []
  );

  const handleOpenDialogForm = (data: any | undefined) => {
    const position = daysFormatIndex[dataAddReservation.indexColumn] ? daysFormatIndex[dataAddReservation.indexColumn] : {};
    
    setDataAddReservation({
      ...dataAddReservationDefine,
      typeAdd: false,
      reserveEdit: data,
      patientEdit: null,
      positionData: {
        starttime: '',
        ...position
      }
    });

    setOpenDialogAddReserve(true);
  };

  const handleCloseDialogEditNote = useCallback(() => {
    setDataNote(dataNoteDefine);
  }, []);

  const formatDoctorsData = () => {
    const doctors = props.dataCalendar.doctors;

    const doctorsObject: any = doctors
      ? doctors.reduce((acc: any, doctor: any) => {
        acc[doctor.id] = doctor;
        return acc;
      }, {} as any)
      : {};

    setDoctorsData(doctorsObject);
  };

  const dateSelect = useCallback(
    (date: string) => {
      return props.dateSelected === dayjs(date).format('YYYY-MM-DD');
    },
    [props.dateSelected]
  );

  const handlePrintDate = useCallback(
    (date: string) => () => {
      setWaiting(true);
      setDataPrint({
        datePrint: date,
        code: generateRandomString(5),
      });
    },
    []
  );

  const formatIndexDoctors = (currentIndex: number, item: any, daysFormat: any) => {
    getField(item, 'doctors', []).forEach((itemDoctor: any) => {
      daysFormat[currentIndex] = {
        day: getField(item, 'day', null),
        doctor: {
          id: getField(itemDoctor, 'id', null),
          op: true
        },
        typeColumn: 'doctor'
      }

      daysFormat[currentIndex + 1] = {
        day: getField(item, 'day', null),
        doctor: {
          id: getField(itemDoctor, 'id', null),
          op: false
        },
        typeColumn: 'doctor'
      }

      currentIndex += 2;
    })

    return {
      currentIndex,
      daysFormat
    };
  };

  const formatIndexMachines = (currentIndex: number, item: any, daysFormat: any) => {
    getField(item, 'machines', []).forEach((itemMachine: any) => {
      daysFormat[currentIndex] = {
        day: getField(item, 'day', null),
        machine: {
          id: getField(itemMachine, 'id', null)
        },
        typeColumn: 'machine'
      }

      currentIndex++;
    })

    return {
      currentIndex,
      daysFormat
    };
  };

  const formatDataDays = (dataDays: any[]) => {
    let daysFormat: any = {};

    let index = 0;
    dataDays.forEach((item: any) => {
      const resultIndexDoctor = formatIndexDoctors(index, item, daysFormat);
      daysFormat = resultIndexDoctor.daysFormat;
      index = resultIndexDoctor.currentIndex;

      const resultIndexMachins = formatIndexMachines(index, item, daysFormat);
      daysFormat = resultIndexMachins.daysFormat;
      index = resultIndexMachins.currentIndex;

      if (getField(item, 'nurse', []).length !== 0) {
        daysFormat[index] = {
          day: getField(item, 'day', null),
          nurse: true,
          typeColumn: 'nurse'
        }
        index++;
      }

      if (getField(item, 'reception', []).length !== 0) {
        daysFormat[index] = {
          day: getField(item, 'day', null),
          reception: true,
          typeColumn: 'reception'
        }
        index++;
      }

    })

    setDaysFormatIndex(daysFormat)
  };

  useEffect(() => {
    if (props.dataCalendar.days.length !== 0) {
      setIsLoading(false);
      formatDoctorsData();

      formatDataDays(props.dataCalendar.days);
    }

    // eslint-disable-next-line
  }, [props.dataCalendar.days]);

  useEffect(() => {
    setDataNotes(props.dataCalendar.notes);
  }, [props.dataCalendar.notes]);

  return (
    <>
      <Paper
        sx={{
          bgcolor: store.palette.container.paper,
          borderRadius: '16px',
          mt: '10px',
          height: 'calc(100vh - 150px)',
          width: props.openFilter ? 'calc(100vw - 355px)' : 'calc(100vw - 190px)',
          overflow: 'hidden',
        }}
      >
        {!isLoading ? (
          <TableContainer
            sx={{
              boxShadow: 'none',
              '& *': { color: 'primary.dark', fontSize: '12px' },
              width: '100%',
              minWidth: 'calc(100vw - 355px)',
              height: '100%',
            }}
          >
            <Table
              stickyHeader
              sx={{
                '& .MuiTableCell-root.MuiTableCell-head': {
                  borderBottom: `1px solid ${colorBorderTable}`,
                  borderRight: `3px solid ${colorBorderTable}`,
                  fontWeight: 700,
                  fontSize: '12px',
                  p: '0px 8px',
                  overflow: 'auto',
                  '&:first-of-type': {
                    ...stickyCol,
                    zIndex: 3,
                  },
                },
                '& .MuiTableCell-root': {
                  borderRight: `3px solid ${colorBorderTable}`,
                },
                '& .MuiTableCell-root.MuiTableCell-body': {
                  p: '0px 8px',
                  '&:first-of-type': {
                    ...stickyCol,
                  },
                },
              }}
              aria-label='simple table'
            >
              <TableHead className='calendar-thead' sx={stickyHeader}>
                <TableRow sx={{ height: '52px' }}>
                  <th
                    align='center'
                  />
                  {dataDays && (
                    <DaysCalendarHeaderFirst
                      daysCal={dataDays}
                      handlePrintDate={handlePrintDate}
                      dateSelect={dateSelect}
                    />
                  )}
                </TableRow>

                <TableRow sx={{ height: '36px', top: '52px' }}>
                  <th
                    align='center'
                  />
                  {dataDays && (
                    <DaysCalendarHeaderSecond
                      daysCal={dataDays}
                      doctorsData={doctorsData}
                    />
                  )}
                </TableRow>

                <TableRow
                  sx={{
                    height: '100px',
                    cursor: 'pointer',
                  }}
                >
                  <th
                    align='center'
                    style={{
                      minWidth: '46px',
                      // borderRight: props.dataCalendar.days
                      //   && props.dataCalendar.days[0]
                      //   ? getBorderStyle(props.dataCalendar.days[0]) : ''
                    }}
                  />
                  {dataNotes && dataDays && (
                    <DaysCalendarHeaderThird
                      daysCal={dataDays}
                      handleOpenDialogEditNote={handleOpenDialogEditNote}
                      dataNote={dataNotes}
                    />
                  )}
                </TableRow>

                <TableRow
                  sx={{ height: '25px' }}
                >
                  <th
                    align='center'
                    style={{
                      borderBottom: '1px solid #DDD',
                    }}
                  />
                  {dataDays && (
                    <DaysCalendarHeaderFour daysCal={dataDays} />
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {dataTimes && (
                  <DataCalendarTimes
                    daysTimes={dataTimes}
                    handleOpenDialogDetail={handleOpenDialogDetail}
                    handleOpenDialogAddReserve={handleOpenDialogAddReserve}
                  />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Skeleton
            variant='rectangular'
            sx={{ width: '100%', height: 'calc(100vh - 150px)' }}
          />
        )}

        <Box display={'none'}>
          <PrintDateCalendar
            dataCalendar={props.dataCalendar}
            dataPrint={dataPrint}
            onComplate={useCallback(() => setWaiting(false), [])}
          />
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={waiting}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      </Paper>
      <DialogEditNote
        data={dataNote}
        onClose={handleCloseDialogEditNote}
        onSave={props.onChangeNote}
      />
      {/* <DialogForm
        open={dataDialogForm.open}
        data={dataDialogForm.data}
        baseData={baseData}
        onClose={handleCloseDialogForm}
      /> */}
      <DialogDetail
        open={dataDialogDetail}
        patientId={detailPatientid}
        id={detailId}
        onClose={handleCloseDialogDetail}
        onEdit={handleOpenDialogForm}
      />
      <DialogAddReserve
        open={openDialogAddReserve}
        onClose={handleCloseDialogAddReserve}
        data={dataAddReservation}
      />
    </>
  );
};
// const CalendarMemo = memo(Calendar);

export default Calendar;
